import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ArrowDownRight,
  ArrowUpRight,
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  Minus,
  Plus,
  RotateCcw,
  Search
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import axiosInstance from '../config/axios';
import { EXPORT_FAILED_MESSAGE, getFriendlyErrorMessage } from '../utils/errorMessages';
import {
  buildMovementBreakdown,
  buildSiteMovementBreakdown,
  buildTopVariances,
  buildValueByGroup,
  filterFullInventoryLines,
  formatQuantityDisplay,
  MOVEMENT_HELP_ITEMS,
  resolveCountQuantityDisplay,
  uniqueSortedField,
  ZERO_ROW_CAVEAT_TEXT
} from '../utils/fullInventoryFilters';
import './FullInventoryReport.css';
import BulkExportFullCounts from './BulkExportFullCounts';
import { useAuth } from '../context/AuthContext';

const MOVEMENT_COLORS = {
  increased: '#10b981',
  decreased: '#ef4444',
  unchanged: '#94a3b8',
  new: '#10b981',
  missing: '#ef4444',
  added: '#10b981',
  removed: '#ef4444'
};

function titleCaseMovement(value) {
  const raw = String(value || '').trim();
  if (!raw) return '—';
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}

function warningSiteSummary(warning) {
  const sites = Array.isArray(warning?.sites) ? warning.sites : [];
  if (!sites.length) return '';
  const names = sites
    .slice(0, 8)
    .map((s) => s.siteName || s.siteId)
    .filter(Boolean);
  const more = sites.length > names.length ? ` (+${sites.length - names.length} more)` : '';
  return names.join(', ') + more;
}

function fmtNum(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  return Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtPct(n) {
  if (n == null || Number.isNaN(Number(n))) return '—';
  const sign = Number(n) > 0 ? '+' : '';
  return `${sign}${Number(n).toFixed(2)}%`;
}

function fmtDate(d) {
  if (!d) return '—';
  try {
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return '—';
    return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return '—';
  }
}

function DirectionIcon({ direction }) {
  if (direction === 'increased' || direction === 'added') return <ArrowUpRight size={14} aria-hidden />;
  if (direction === 'decreased' || direction === 'removed') return <ArrowDownRight size={14} aria-hidden />;
  if (direction === 'new') return <Plus size={14} aria-hidden />;
  if (direction === 'missing') return <Minus size={14} aria-hidden />;
  return <Check size={14} aria-hidden />;
}

const emptyLineFilters = () => ({
  search: '',
  brand: '',
  category: '',
  flagsOnly: false,
  changedOnly: false,
  increasedOnly: false,
  decreasedOnly: false,
  newMissingOnly: false,
  addedOnly: false,
  removedOnly: false,
  highVarianceOnly: false,
  priorityOnly: false
});

const FullInventoryReport = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [reportScope, setReportScope] = useState('site'); // 'site' | 'brand'
  const [sites, setSites] = useState([]);
  const [brands, setBrands] = useState([]);
  const [counts, setCounts] = useState([]);
  const [siteId, setSiteId] = useState('');
  const [brandId, setBrandId] = useState('');
  const [countId, setCountId] = useState('');
  const [compareEnabled, setCompareEnabled] = useState(false);
  const [compareCountId, setCompareCountId] = useState('');
  const [report, setReport] = useState(null);
  const [sitesLoading, setSitesLoading] = useState(true);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [countsLoading, setCountsLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [exportError, setExportError] = useState('');
  const [lineFilters, setLineFilters] = useState(emptyLineFilters);
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const [groupBy, setGroupBy] = useState('category');
  const [showMovementHelp, setShowMovementHelp] = useState(false);

  const isBrandScope = reportScope === 'brand';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setSitesLoading(true);
      setError('');
      try {
        const res = await axiosInstance.get('/api/tenant/reports/full-inventory/sites');
        if (!cancelled) setSites(res.data?.sites || []);
      } catch (err) {
        if (!cancelled) {
          setError(
            getFriendlyErrorMessage(err, { kind: 'load' }) ||
              'We could not load sites for this report. Please try again.'
          );
          setSites([]);
        }
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!isBrandScope) return undefined;
    let cancelled = false;
    (async () => {
      setBrandsLoading(true);
      setError('');
      try {
        const res = await axiosInstance.get('/api/tenant/reports/full-inventory/brands');
        if (!cancelled) setBrands(res.data?.brands || []);
      } catch (err) {
        if (!cancelled) {
          setError(
            getFriendlyErrorMessage(err, { kind: 'load' }) ||
              'We could not load brands for this report. Please try again.'
          );
          setBrands([]);
        }
      } finally {
        if (!cancelled) setBrandsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isBrandScope]);

  useEffect(() => {
    if (isBrandScope) return undefined;
    if (!siteId) {
      setCounts([]);
      setCountId('');
      setCompareCountId('');
      setReport(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setCountsLoading(true);
      setError('');
      setCountId('');
      setCompareCountId('');
      setReport(null);
      try {
        const res = await axiosInstance.get(
          `/api/tenant/reports/full-inventory/counts?siteId=${encodeURIComponent(siteId)}`
        );
        if (!cancelled) setCounts(res.data?.counts || []);
      } catch (err) {
        if (!cancelled) {
          setError(
            getFriendlyErrorMessage(err, { kind: 'load' }) ||
              'We could not load counts for this site. Please try again.'
          );
          setCounts([]);
        }
      } finally {
        if (!cancelled) setCountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [siteId, isBrandScope]);

  useEffect(() => {
    if (!isBrandScope) return undefined;
    if (!brandId) {
      setCounts([]);
      setCountId('');
      setCompareCountId('');
      setReport(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      setCountsLoading(true);
      setError('');
      setCountId('');
      setCompareCountId('');
      setReport(null);
      try {
        const res = await axiosInstance.get(
          `/api/tenant/reports/full-inventory/brand-counts?brandId=${encodeURIComponent(brandId)}`
        );
        if (!cancelled) setCounts(res.data?.counts || []);
      } catch (err) {
        if (!cancelled) {
          setError(
            getFriendlyErrorMessage(err, { kind: 'load' }) ||
              'We could not load count periods for this brand. Please try again.'
          );
          setCounts([]);
        }
      } finally {
        if (!cancelled) setCountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [brandId, isBrandScope]);

  const switchScope = (nextScope) => {
    if (nextScope === reportScope) return;
    setReportScope(nextScope);
    setSiteId('');
    setBrandId('');
    setCounts([]);
    setCountId('');
    setCompareEnabled(false);
    setCompareCountId('');
    setReport(null);
    setLineFilters(emptyLineFilters());
    setExpandedKeys(new Set());
    setError('');
    setExportError('');
    setGroupBy('category');
  };

  const loadReport = useCallback(async () => {
    if (isBrandScope) {
      if (!brandId || !countId) return;
    } else if (!siteId || !countId) {
      return;
    }
    if (compareEnabled && !compareCountId) {
      setError('Please select a second count to compare, or turn off comparison.');
      return;
    }
    setReportLoading(true);
    setError('');
    setExportError('');
    setExpandedKeys(new Set());
    try {
      const params = new URLSearchParams({ countId });
      if (compareEnabled && compareCountId) params.append('compareCountId', compareCountId);
      let url;
      if (isBrandScope) {
        params.append('brandId', brandId);
        url = `/api/tenant/reports/full-inventory/brand?${params.toString()}`;
      } else {
        params.append('siteId', siteId);
        url = `/api/tenant/reports/full-inventory?${params.toString()}`;
      }
      const res = await axiosInstance.get(url);
      setReport(res.data || null);
    } catch (err) {
      setReport(null);
      setError(
        getFriendlyErrorMessage(err, { kind: 'load' }) ||
          'We could not load the full inventory report. Please try again.'
      );
    } finally {
      setReportLoading(false);
    }
  }, [isBrandScope, siteId, brandId, countId, compareEnabled, compareCountId]);

  const exportExcel = useCallback(async () => {
    if (isBrandScope) {
      if (!brandId || !countId) return;
    } else if (!siteId || !countId) {
      return;
    }
    if (compareEnabled && !compareCountId) {
      setExportError('Please select a second count to compare before exporting.');
      return;
    }
    setExporting(true);
    setExportError('');
    try {
      const params = new URLSearchParams({ countId });
      if (compareEnabled && compareCountId) params.append('compareCountId', compareCountId);
      let url;
      if (isBrandScope) {
        params.append('brandId', brandId);
        url = `/api/tenant/reports/full-inventory/brand/export-excel?${params.toString()}`;
      } else {
        params.append('siteId', siteId);
        url = `/api/tenant/reports/full-inventory/export-excel?${params.toString()}`;
      }
      const res = await axiosInstance.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      let filename = 'FullInventoryReport.xlsx';
      const disposition = res.headers?.['content-disposition'] || res.headers?.['Content-Disposition'];
      if (disposition) {
        const match = String(disposition).match(/filename="?([^"]+)"?/i);
        if (match && match[1]) filename = match[1];
      }
      const href = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(href);
    } catch (err) {
      setExportError(
        getFriendlyErrorMessage(err, { kind: 'export' }) || EXPORT_FAILED_MESSAGE
      );
    } finally {
      setExporting(false);
    }
  }, [isBrandScope, siteId, brandId, countId, compareEnabled, compareCountId]);

  const resetFilters = () => {
    setSiteId('');
    setBrandId('');
    setCountId('');
    setCompareEnabled(false);
    setCompareCountId('');
    setReport(null);
    setLineFilters(emptyLineFilters());
    setExpandedKeys(new Set());
    setError('');
    setExportError('');
  };

  const compareCountOptions = useMemo(
    () => counts.filter((c) => c.id !== countId),
    [counts, countId]
  );

  const isCompare = report?.mode === 'compare';
  const isBrandReport = report?.scope === 'brand';
  const allLines = useMemo(() => report?.lines || [], [report]);

  const brandOptions = useMemo(() => uniqueSortedField(allLines, 'brandName'), [allLines]);
  const categoryOptions = useMemo(() => uniqueSortedField(allLines, 'category'), [allLines]);

  const filteredLines = useMemo(
    () => filterFullInventoryLines(allLines, lineFilters),
    [allLines, lineFilters]
  );

  const groupedLines = useMemo(() => {
    const groups = new Map();
    for (const line of filteredLines) {
      const brand = line.brandName || 'Unbranded';
      const cat = line.category || 'Uncategorized';
      const key = `${brand}||${cat}`;
      if (!groups.has(key)) groups.set(key, { brand, category: cat, lines: [] });
      groups.get(key).lines.push(line);
    }
    return [...groups.values()];
  }, [filteredLines]);

  const valueByGroup = useMemo(
    () =>
      buildValueByGroup(
        allLines,
        groupBy === 'brand' ? 'brandName' : 'category',
        isCompare ? 'compare' : 'single'
      ),
    [allLines, groupBy, isCompare]
  );

  const movementData = useMemo(
    () => (isCompare ? buildMovementBreakdown(report?.summary) : []),
    [isCompare, report]
  );

  const siteMovementData = useMemo(
    () => (isCompare && isBrandReport ? buildSiteMovementBreakdown(allLines) : []),
    [isCompare, isBrandReport, allLines]
  );

  const topVariances = useMemo(
    () => (isCompare ? buildTopVariances(allLines, 10) : []),
    [isCompare, allLines]
  );

  const coverageNote = useMemo(() => {
    if (!report || !isBrandReport) return null;
    const cov = isCompare ? report.coverage?.count1 : report.coverage;
    if (!cov?.partialCoverage) return null;
    const missing = Number(cov.sitesMissingCount) || 0;
    if (!missing) return null;
    return `Some sites may not have submitted counts for this period (${missing} site${
      missing === 1 ? '' : 's'
    } missing).`;
  }, [report, isBrandReport, isCompare]);

  const reportWarnings = useMemo(() => {
    const list = Array.isArray(report?.warnings) ? report.warnings : [];
    return list;
  }, [report]);

  const reportNotes = useMemo(() => {
    const list = Array.isArray(report?.notes) ? [...report.notes] : [];
    if (isCompare && !list.some((n) => n.type === 'zero_row_caveat')) {
      list.push({ type: 'zero_row_caveat', message: ZERO_ROW_CAVEAT_TEXT });
    }
    return list;
  }, [report, isCompare]);

  const emptyHint = isBrandScope
    ? !brandId
      ? brandsLoading
        ? null
        : brands.length
          ? 'Select a brand to begin.'
          : 'No brands found for this tenant.'
      : !countId
        ? countsLoading
          ? null
          : counts.length
            ? 'Select a count period to view the brand inventory report.'
            : 'No counts found for this brand.'
        : compareEnabled && !compareCountId
          ? 'Select a second count period to compare, then apply.'
          : !report && !reportLoading
            ? 'Click Apply to load the report.'
            : null
    : !siteId
      ? 'Select a site to begin.'
      : !countId
        ? 'Select a count to view the full inventory report.'
        : compareEnabled && !compareCountId
          ? 'Select a second count to compare, then apply.'
          : !report && !reportLoading
            ? 'Click Apply to load the report.'
            : null;

  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const setLineFilter = (patch) => setLineFilters((f) => ({ ...f, ...patch }));

  const canLoad = isBrandScope
    ? !!(brandId && countId && (!compareEnabled || compareCountId))
    : !!(siteId && countId && (!compareEnabled || compareCountId));

  const detailColSpanBase = isCompare ? 13 : 11;
  const detailColSpan = isBrandReport ? detailColSpanBase + 1 : detailColSpanBase;

  return (
    <div className="fir-root">
      {error && (
        <div className="error fir-alert" role="alert">
          {error}
        </div>
      )}
      {exportError && (
        <div className="error fir-alert" role="alert">
          <strong>Export failed.</strong> {exportError}
        </div>
      )}

      <div className="card fir-filters-card">
        <div className="fir-filters-card-header">
          <div>
            <h3 className="fir-filters-card-title">Full Inventory Report</h3>
            <p className="fir-filters-card-subtitle">
              {isBrandScope
                ? 'Aggregate inventory across all sites for a selected brand, with optional period comparison.'
                : 'View a complete count for one site, or compare two counts for the same site.'}
            </p>
          </div>
          <button
            type="button"
            className="fir-btn-export-outline"
            onClick={exportExcel}
            disabled={!canLoad || exporting || reportLoading}
            title="Export Excel"
          >
            <Download size={16} aria-hidden />
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>

        <div className="fir-scope-row" role="group" aria-label="Report scope">
          <span className="fir-scope-label">Report scope</span>
          <div className="fir-scope-toggle">
            <button
              type="button"
              className={!isBrandScope ? 'active' : ''}
              onClick={() => switchScope('site')}
            >
              Single site
            </button>
            <button
              type="button"
              className={isBrandScope ? 'active' : ''}
              onClick={() => switchScope('brand')}
            >
              All sites by brand
            </button>
          </div>
        </div>

        <div className="fir-filters-grid">
          {!isBrandScope && (
            <div className="fir-filter-group">
              <label htmlFor="fir-site">Site</label>
              <select
                id="fir-site"
                value={siteId}
                disabled={sitesLoading}
                onChange={(e) => setSiteId(e.target.value)}
              >
                <option value="">{sitesLoading ? 'Loading sites…' : 'Select a site'}</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                    {s.buCode ? ` — ${s.buCode}` : ''}
                    {s.isArchivedOnly ? ' (archived)' : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isBrandScope && (
            <div className="fir-filter-group">
              <label htmlFor="fir-brand">Brand</label>
              <select
                id="fir-brand"
                value={brandId}
                disabled={brandsLoading}
                onChange={(e) => setBrandId(e.target.value)}
              >
                <option value="">
                  {brandsLoading
                    ? 'Loading brands…'
                    : brands.length
                      ? 'Select a brand'
                      : 'No brands found'}
                </option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                    {b.liveSiteCount != null ? ` (${b.liveSiteCount} sites)` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="fir-filter-group">
            <label htmlFor="fir-count1">{isBrandScope ? 'Count 1 period' : 'Count 1'}</label>
            <select
              id="fir-count1"
              value={countId}
              disabled={isBrandScope ? !brandId || countsLoading : !siteId || countsLoading}
              onChange={(e) => {
                setCountId(e.target.value);
                setReport(null);
              }}
            >
              <option value="">
                {isBrandScope
                  ? !brandId
                    ? 'Select a brand first'
                    : countsLoading
                      ? 'Loading periods…'
                      : counts.length
                        ? 'Select a count period'
                        : 'No counts found for this brand'
                  : !siteId
                    ? 'Select a site first'
                    : countsLoading
                      ? 'Loading counts…'
                      : counts.length
                        ? 'Select a count'
                        : 'No completed counts for this site'}
              </option>
              {counts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>

          <div className="fir-filter-group fir-compare-toggle">
            <label htmlFor="fir-compare-switch">Compare counts</label>
            <button
              id="fir-compare-switch"
              type="button"
              role="switch"
              aria-checked={compareEnabled}
              className={'fir-switch' + (compareEnabled ? ' on' : '')}
              onClick={() => {
                setCompareEnabled((v) => !v);
                setCompareCountId('');
                setReport(null);
              }}
              disabled={isBrandScope ? !brandId : !siteId}
            >
              <span className="fir-switch-knob" />
              <span className="fir-switch-label">{compareEnabled ? 'On' : 'Off'}</span>
            </button>
          </div>

          {compareEnabled && (
            <div className="fir-filter-group">
              <label htmlFor="fir-count2">{isBrandScope ? 'Count 2 period' : 'Count 2'}</label>
              <select
                id="fir-count2"
                value={compareCountId}
                disabled={!countId || countsLoading}
                onChange={(e) => {
                  setCompareCountId(e.target.value);
                  setReport(null);
                }}
              >
                <option value="">
                  {!countId
                    ? isBrandScope
                      ? 'Select Count 1 period first'
                      : 'Select Count 1 first'
                    : isBrandScope
                      ? 'Select a second count period'
                      : 'Select a second count'}
                </option>
                {compareCountOptions.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <div className="fir-filter-actions">
          <button
            type="button"
            className="fir-btn-apply"
            onClick={loadReport}
            disabled={reportLoading || !canLoad}
          >
            {reportLoading ? 'Loading…' : 'Apply'}
          </button>
          <button type="button" className="fir-btn-reset" onClick={resetFilters}>
            <RotateCcw size={14} aria-hidden /> Reset
          </button>
        </div>
      </div>

      {emptyHint && !report && !reportLoading && (
        <div className="card fir-empty-card">
          <p>{emptyHint}</p>
        </div>
      )}

      {reportLoading && (
        <div className="card fir-empty-card">
          <p>Loading report…</p>
        </div>
      )}

      {report && !reportLoading && (
        <>
          <div className="card fir-meta-card">
            <div className="fir-meta-grid">
              {isBrandReport ? (
                <>
                  <div>
                    <span className="fir-meta-label">Scope</span>
                    <strong>All sites by brand</strong>
                  </div>
                  <div>
                    <span className="fir-meta-label">Brand</span>
                    <strong>{report.brand?.name || '—'}</strong>
                  </div>
                  <div>
                    <span className="fir-meta-label">Sites included</span>
                    <strong>
                      {fmtNum(
                        isCompare
                          ? report.summary?.sitesIncludedCount
                          : report.coverage?.sitesIncludedCount ?? report.summary?.sitesIncludedCount
                      )}
                    </strong>
                  </div>
                </>
              ) : (
                <div>
                  <span className="fir-meta-label">Site</span>
                  <strong>
                    {report.site?.name || '—'}
                    {report.site?.buCode ? ` (${report.site.buCode})` : ''}
                  </strong>
                </div>
              )}
              {!isCompare && (
                <>
                  <div>
                    <span className="fir-meta-label">Period</span>
                    <strong>{report.count?.periodName || '—'}</strong>
                  </div>
                  <div>
                    <span className="fir-meta-label">Count date</span>
                    <strong>{fmtDate(report.count?.countDate)}</strong>
                  </div>
                  <div>
                    <span className="fir-meta-label">Source</span>
                    <strong>{report.count?.source === 'archive' ? 'Archived' : 'Live'}</strong>
                  </div>
                  {!isBrandReport && (
                    <>
                      <div>
                        <span className="fir-meta-label">Manager</span>
                        <strong>{report.count?.managerName || '—'}</strong>
                      </div>
                      <div>
                        <span className="fir-meta-label">Submitted by</span>
                        <strong>{report.count?.personName || '—'}</strong>
                      </div>
                    </>
                  )}
                </>
              )}
              {isCompare && (
                <>
                  <div>
                    <span className="fir-meta-label">Count 1</span>
                    <strong>{report.count1?.label || '—'}</strong>
                  </div>
                  <div>
                    <span className="fir-meta-label">Count 2</span>
                    <strong>{report.count2?.label || '—'}</strong>
                  </div>
                </>
              )}
            </div>
            {(coverageNote || reportWarnings.length > 0 || reportNotes.length > 0) && (
              <div className="fir-warnings" role="status">
                {coverageNote ? <p className="fir-coverage-note">{coverageNote}</p> : null}
                {reportWarnings.map((warning, idx) => {
                  const sitesText = warningSiteSummary(warning);
                  return (
                    <div key={`${warning.type || 'warn'}-${idx}`} className="fir-warning-item">
                      <p className="fir-warning-message">{warning.message}</p>
                      {sitesText ? (
                        <p className="fir-warning-sites">
                          Affected sites: {sitesText}
                          {warning.periodName ? ` · ${warning.periodName}` : ''}
                        </p>
                      ) : null}
                    </div>
                  );
                })}
                {reportNotes.map((note, idx) => (
                  <p key={`${note.type || 'note'}-${idx}`} className="fir-note-item">
                    {note.message}
                  </p>
                ))}
              </div>
            )}
            {!isCompare && report.count?.generalNotes ? (
              <p className="fir-general-notes">
                <span className="fir-meta-label">General notes</span>
                {report.count.generalNotes}
              </p>
            ) : null}
          </div>

          <div className={'fir-stats-row' + (isCompare ? ' fir-stats-compare' : '')}>
            {!isCompare && (
              <>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">Total item lines</span>
                  <span className="fir-stat-value">{fmtNum(report.summary?.totalItemLines)}</span>
                </div>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">Total inventory value</span>
                  <span className="fir-stat-value fir-stat-money">
                    {fmtNum(report.summary?.totalInventoryValue)}
                  </span>
                </div>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">Priority items</span>
                  <span className="fir-stat-value">{fmtNum(report.summary?.priorityItemsCount)}</span>
                </div>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">Priority zero flags</span>
                  <span className="fir-stat-value fir-stat-warn">
                    {fmtNum(report.summary?.priorityZeroFlags)}
                  </span>
                </div>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">High-value flags</span>
                  <span className="fir-stat-value">{fmtNum(report.summary?.highValueFlags)}</span>
                </div>
              </>
            )}
            {isCompare && (
              <>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">Count 1 total value</span>
                  <span className="fir-stat-value fir-stat-money">
                    {fmtNum(report.summary?.count1TotalValue)}
                  </span>
                </div>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">Count 2 total value</span>
                  <span className="fir-stat-value fir-stat-money">
                    {fmtNum(report.summary?.count2TotalValue)}
                  </span>
                </div>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">Value difference</span>
                  <span
                    className={
                      'fir-stat-value' +
                      (Number(report.summary?.valueDifference) < 0
                        ? ' fir-stat-neg'
                        : Number(report.summary?.valueDifference) > 0
                          ? ' fir-stat-pos'
                          : '')
                    }
                  >
                    {fmtNum(report.summary?.valueDifference)}
                  </span>
                </div>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">% Movement</span>
                  <span
                    className={
                      'fir-stat-value' +
                      (Number(report.summary?.percentageMovement) < 0
                        ? ' fir-stat-neg'
                        : Number(report.summary?.percentageMovement) > 0
                          ? ' fir-stat-pos'
                          : '')
                    }
                  >
                    {fmtPct(report.summary?.percentageMovement)}
                  </span>
                </div>
                <div className="fir-stat-card">
                  <span className="fir-stat-label">Increased / Decreased</span>
                  <span className="fir-stat-value fir-stat-split">
                    <span className="fir-up">{report.summary?.linesIncreased || 0}</span>
                    <span className="fir-down">{report.summary?.linesDecreased || 0}</span>
                  </span>
                </div>
                <div className="fir-stat-card fir-stat-card-wide">
                  <span className="fir-stat-label">Unchanged / New / Missing / Added / Removed</span>
                  <span className="fir-stat-value fir-stat-quint">
                    <span className="fir-neutral">{report.summary?.linesUnchanged || 0}</span>
                    <span className="fir-up">{report.summary?.linesNew || 0}</span>
                    <span className="fir-down">{report.summary?.linesMissing || 0}</span>
                    <span className="fir-up">{report.summary?.linesAdded || 0}</span>
                    <span className="fir-down">{report.summary?.linesRemoved || 0}</span>
                  </span>
                </div>
                {isBrandReport && (
                  <div className="fir-stat-card fir-stat-card-wide">
                    <span className="fir-stat-label">Site-level movements</span>
                    <div className="fir-site-movement-counts">
                      {MOVEMENT_HELP_ITEMS.map((item) => {
                        const count =
                          Number(report.summary?.siteMovementSummary?.[item.key]) || 0;
                        return (
                          <span key={item.key} className={`fir-site-move fir-dir-${item.key}`}>
                            {item.label} {count}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <p className="fir-summary-note">Summary cards show full-report totals (not filter-adjusted).</p>

          <div className="fir-charts-row">
            <div className="card fir-chart-card">
              <div className="fir-chart-header">
                <h3 className="fir-chart-title">Value by {groupBy === 'brand' ? 'brand' : 'category'}</h3>
                <div className="fir-chart-toggle" role="group" aria-label="Group chart by">
                  <button
                    type="button"
                    className={groupBy === 'category' ? 'active' : ''}
                    onClick={() => setGroupBy('category')}
                  >
                    Category
                  </button>
                  <button
                    type="button"
                    className={groupBy === 'brand' ? 'active' : ''}
                    onClick={() => setGroupBy('brand')}
                  >
                    Brand
                  </button>
                </div>
              </div>
              <div className="fir-chart-body">
                {valueByGroup.length === 0 ? (
                  <p className="fir-chart-empty">No values to chart.</p>
                ) : (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={valueByGroup} margin={{ top: 8, right: 8, left: 0, bottom: 48 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis dataKey="label" interval={0} angle={-25} textAnchor="end" height={60} tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} />
                      <YAxis tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} />
                      <Tooltip
                        formatter={(v) => fmtNum(v)}
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-card)',
                          color: 'var(--color-text)'
                        }}
                      />
                      <Legend />
                      {isCompare ? (
                        <>
                          <Bar dataKey="count1" name="Count 1" fill="#1a3a6b" radius={[4, 4, 0, 0]} />
                          <Bar dataKey="count2" name="Count 2" fill="#2e6be6" radius={[4, 4, 0, 0]} />
                        </>
                      ) : (
                        <Bar dataKey="value" name="Value" fill="#2e6be6" radius={[4, 4, 0, 0]} />
                      )}
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {isCompare && (
              <div className="card fir-chart-card">
                <div className="fir-chart-header">
                  <h3 className="fir-chart-title">
                    {isBrandReport ? 'Aggregate movement' : 'Movement breakdown'}
                  </h3>
                  <button
                    type="button"
                    className="fir-help-toggle"
                    aria-expanded={showMovementHelp}
                    onClick={() => setShowMovementHelp((v) => !v)}
                  >
                    What do these flags mean?
                  </button>
                </div>
                {showMovementHelp ? (
                  <div className="fir-help-panel" role="region" aria-label="Movement flag explanations">
                    <ul className="fir-help-list">
                      {MOVEMENT_HELP_ITEMS.map((item) => (
                        <li key={item.key}>
                          <strong>{item.label}:</strong> {item.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                <div className="fir-chart-body">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={movementData} layout="vertical" margin={{ top: 8, right: 16, left: 16, bottom: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} />
                      <YAxis type="category" dataKey="label" width={100} tick={{ fontSize: 11, fill: 'var(--chart-tick)' }} />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-card)',
                          color: 'var(--color-text)'
                        }}
                      />
                      <Bar dataKey="value" name="Lines" radius={[0, 4, 4, 0]}>
                        {movementData.map((entry) => (
                          <Cell key={entry.key} fill={MOVEMENT_COLORS[entry.key] || '#64748b'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {isCompare && isBrandReport && (
              <div className="card fir-chart-card">
                <div className="fir-chart-header">
                  <h3 className="fir-chart-title">Site-level movements</h3>
                </div>
                <div className="fir-chart-body">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart
                      data={siteMovementData}
                      layout="vertical"
                      margin={{ top: 8, right: 16, left: 16, bottom: 8 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" />
                      <XAxis
                        type="number"
                        allowDecimals={false}
                        tick={{ fontSize: 11, fill: 'var(--chart-tick)' }}
                      />
                      <YAxis
                        type="category"
                        dataKey="label"
                        width={100}
                        tick={{ fontSize: 11, fill: 'var(--chart-tick)' }}
                      />
                      <Tooltip
                        contentStyle={{
                          borderRadius: 8,
                          border: '1px solid var(--color-border)',
                          background: 'var(--color-card)',
                          color: 'var(--color-text)'
                        }}
                      />
                      <Bar dataKey="value" name="Site movements" radius={[0, 4, 4, 0]}>
                        {siteMovementData.map((entry) => (
                          <Cell
                            key={entry.key}
                            fill={MOVEMENT_COLORS[entry.key] || '#64748b'}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {isCompare && (
              <div className="card fir-chart-card fir-top-var-card">
                <div className="fir-chart-header">
                  <h3 className="fir-chart-title">Top 10 value variances</h3>
                </div>
                <div className="fir-top-var-list">
                  {topVariances.length === 0 ? (
                    <p className="fir-chart-empty">No variance lines.</p>
                  ) : (
                    topVariances.map((row, idx) => (
                      <div key={`${row.itemName}-${idx}`} className="fir-top-var-row">
                        <div className="fir-top-var-meta">
                          <strong>{row.itemName}</strong>
                          {row.vendorItemCode ? <span>{row.vendorItemCode}</span> : null}
                        </div>
                        <div className={'fir-top-var-vals' + (row.valueDiff >= 0 ? ' up' : ' down')}>
                          <span>{fmtNum(row.valueDiff)}</span>
                          <span>{fmtPct(row.variancePct)}</span>
                        </div>
                        <div className="fir-top-var-bar-track">
                          <div
                            className={'fir-top-var-bar' + (row.valueDiff >= 0 ? ' up' : ' down')}
                            style={{
                              width: `${Math.min(
                                100,
                                (Math.abs(row.valueDiff) /
                                  Math.max(1, Math.abs(topVariances[0].valueDiff))) *
                                  100
                              )}%`
                            }}
                          />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="card fir-line-filters-card">
            <h3 className="fir-filters-title">Line filters</h3>
            <div className="fir-line-filters-grid">
              <div className="fir-filter-group fir-filter-search">
                <label htmlFor="fir-search">Search</label>
                <div className="fir-search-wrap">
                  <Search size={16} aria-hidden />
                  <input
                    id="fir-search"
                    type="search"
                    placeholder="Search items, vendor codes…"
                    value={lineFilters.search}
                    onChange={(e) => setLineFilter({ search: e.target.value })}
                  />
                </div>
              </div>
              {!isBrandReport && (
                <div className="fir-filter-group">
                  <label htmlFor="fir-line-brand">Brand</label>
                  <select
                    id="fir-line-brand"
                    value={lineFilters.brand}
                    onChange={(e) => setLineFilter({ brand: e.target.value })}
                  >
                    <option value="">All brands</option>
                    {brandOptions.map((b) => (
                      <option key={b} value={b}>
                        {b}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="fir-filter-group">
                <label htmlFor="fir-category">Category</label>
                <select
                  id="fir-category"
                  value={lineFilters.category}
                  onChange={(e) => setLineFilter({ category: e.target.value })}
                >
                  <option value="">All categories</option>
                  {categoryOptions.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="fir-chip-filters">
              <label className={'fir-chip' + (lineFilters.flagsOnly ? ' on' : '')}>
                <input
                  type="checkbox"
                  checked={lineFilters.flagsOnly}
                  onChange={(e) => setLineFilter({ flagsOnly: e.target.checked })}
                />
                Flags
              </label>
              <label className={'fir-chip' + (lineFilters.priorityOnly ? ' on' : '')}>
                <input
                  type="checkbox"
                  checked={lineFilters.priorityOnly}
                  onChange={(e) => setLineFilter({ priorityOnly: e.target.checked })}
                />
                Priority only
              </label>
              {isCompare && (
                <>
                  <label className={'fir-chip' + (lineFilters.changedOnly ? ' on' : '')}>
                    <input
                      type="checkbox"
                      checked={lineFilters.changedOnly}
                      onChange={(e) => setLineFilter({ changedOnly: e.target.checked })}
                    />
                    Changed only
                  </label>
                  <label className={'fir-chip' + (lineFilters.increasedOnly ? ' on' : '')}>
                    <input
                      type="checkbox"
                      checked={lineFilters.increasedOnly}
                      onChange={(e) => setLineFilter({ increasedOnly: e.target.checked })}
                    />
                    Increased
                  </label>
                  <label className={'fir-chip' + (lineFilters.decreasedOnly ? ' on' : '')}>
                    <input
                      type="checkbox"
                      checked={lineFilters.decreasedOnly}
                      onChange={(e) => setLineFilter({ decreasedOnly: e.target.checked })}
                    />
                    Decreased
                  </label>
                  <label className={'fir-chip' + (lineFilters.newMissingOnly ? ' on' : '')}>
                    <input
                      type="checkbox"
                      checked={lineFilters.newMissingOnly}
                      onChange={(e) => setLineFilter({ newMissingOnly: e.target.checked })}
                    />
                    New / Missing
                  </label>
                  <label className={'fir-chip' + (lineFilters.addedOnly ? ' on' : '')}>
                    <input
                      type="checkbox"
                      checked={lineFilters.addedOnly}
                      onChange={(e) => setLineFilter({ addedOnly: e.target.checked })}
                    />
                    Added
                  </label>
                  <label className={'fir-chip' + (lineFilters.removedOnly ? ' on' : '')}>
                    <input
                      type="checkbox"
                      checked={lineFilters.removedOnly}
                      onChange={(e) => setLineFilter({ removedOnly: e.target.checked })}
                    />
                    Removed
                  </label>
                  <label className={'fir-chip' + (lineFilters.highVarianceOnly ? ' on' : '')}>
                    <input
                      type="checkbox"
                      checked={lineFilters.highVarianceOnly}
                      onChange={(e) => setLineFilter({ highVarianceOnly: e.target.checked })}
                    />
                    High variance
                  </label>
                </>
              )}
              <button
                type="button"
                className="fir-chip-clear"
                onClick={() => setLineFilters(emptyLineFilters())}
              >
                Clear line filters
              </button>
            </div>
          </div>

          <div className="card fir-table-card">
            <div className="fir-table-header">
              <h3 className="fir-table-title">Inventory lines</h3>
              <span className="fir-table-count">
                Showing {filteredLines.length.toLocaleString()} of {allLines.length.toLocaleString()} lines
              </span>
            </div>

            {groupedLines.length === 0 ? (
              <p className="fir-table-empty">No lines match your filters.</p>
            ) : (
              <div className="fir-table-scroll">
                <table className={'table fir-table' + (isCompare ? ' fir-table-compare' : '')}>
                  <thead>
                    <tr>
                      <th className="fir-sticky-col fir-expand-col" aria-label="Expand" />
                      <th className="fir-sticky-col fir-sticky-item">Item</th>
                      <th>Vendor code</th>
                      <th>Pack size</th>
                      <th>Location</th>
                      {isBrandReport && <th className="fir-num">Sites</th>}
                      {!isCompare && (
                        <>
                          <th className="fir-num">Qty 1</th>
                          <th className="fir-num">Value 1</th>
                          <th className="fir-num">Qty 2</th>
                          <th className="fir-num">Value 2</th>
                          <th className="fir-num">Line value</th>
                          <th>Flags</th>
                        </>
                      )}
                      {isCompare && (
                        <>
                          <th className="fir-num">C1 Qty</th>
                          <th className="fir-num">C1 Value</th>
                          <th className="fir-num">C2 Qty</th>
                          <th className="fir-num">C2 Value</th>
                          <th className="fir-num">Diff Qty</th>
                          <th className="fir-num">Diff Value</th>
                          <th className="fir-num">% Var</th>
                          <th>Change</th>
                        </>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {groupedLines.map((group) => (
                      <React.Fragment key={`${group.brand}-${group.category}`}>
                        <tr className="fir-group-row">
                          <td colSpan={detailColSpan}>
                            <strong>{group.brand}</strong>
                            <span className="fir-group-sep">·</span>
                            {group.category}
                          </td>
                        </tr>
                        {group.lines.map((line, idx) => {
                          const rowKey =
                            line.matchKey ||
                            `${line.inventoryItemId || 'x'}-${idx}-${line.itemName || ''}`;
                          const dir = line.direction || 'unchanged';
                          const expanded = expandedKeys.has(rowKey);
                          return (
                            <React.Fragment key={rowKey}>
                              <tr
                                className={
                                  'fir-row' +
                                  (line.highVariance ? ' fir-row-high-var' : '') +
                                  (dir === 'increased' || dir === 'added' || dir === 'new'
                                    ? ' fir-row-up'
                                    : '') +
                                  (dir === 'decreased' || dir === 'removed' || dir === 'missing'
                                    ? ' fir-row-down'
                                    : '') +
                                  (expanded ? ' fir-row-expanded' : '')
                                }
                              >
                                <td className="fir-sticky-col fir-expand-col">
                                  <button
                                    type="button"
                                    className="fir-expand-btn"
                                    aria-expanded={expanded}
                                    aria-label={expanded ? 'Collapse details' : 'Expand details'}
                                    onClick={() => toggleExpanded(rowKey)}
                                  >
                                    {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                  </button>
                                </td>
                                <td className="fir-sticky-col fir-sticky-item">
                                  <div className="fir-item-cell">
                                    <span className="fir-item-name">
                                      {line.itemName || '—'}
                                      {line.priority ? (
                                        <span className="fir-badge fir-badge-priority">Priority</span>
                                      ) : null}
                                    </span>
                                    {line.itemDescription ? (
                                      <span className="fir-item-desc">{line.itemDescription}</span>
                                    ) : null}
                                  </div>
                                </td>
                                <td>{line.vendorItemCode || '—'}</td>
                                <td>{line.packSize || '—'}</td>
                                <td>{line.location || '—'}</td>
                                {isBrandReport && (
                                  <td className="fir-num">{fmtNum(line.siteCount)}</td>
                                )}
                                {!isCompare && (
                                  <>
                                    <td className="fir-num">
                                      {fmtNum(line.countedQuantity)}
                                      {line.unit1 ? <span className="fir-unit"> {line.unit1}</span> : null}
                                    </td>
                                    <td className="fir-num">{fmtNum(line.countedValue1)}</td>
                                    <td className="fir-num">
                                      {fmtNum(line.countedQuantity2)}
                                      {line.unit2 ? <span className="fir-unit"> {line.unit2}</span> : null}
                                    </td>
                                    <td className="fir-num">{fmtNum(line.countedValue2)}</td>
                                    <td className="fir-num fir-strong">{fmtNum(line.lineValue)}</td>
                                    <td>
                                      <div className="fir-flags">
                                        {line.isPriorityZero ? (
                                          <span className="fir-badge fir-badge-warn">Zero priority</span>
                                        ) : null}
                                        {line.isHighValue ? (
                                          <span className="fir-badge fir-badge-hv">High value</span>
                                        ) : null}
                                        {!line.isPriorityZero && !line.isHighValue ? '—' : null}
                                      </div>
                                    </td>
                                  </>
                                )}
                                {isCompare && (
                                  <>
                                    <td className="fir-num fir-qty-pair">
                                      {line.count1
                                        ? resolveCountQuantityDisplay(line.count1)
                                        : '—'}
                                    </td>
                                    <td className="fir-num">
                                      {line.count1 ? fmtNum(line.count1.lineValue) : '—'}
                                    </td>
                                    <td className="fir-num fir-qty-pair">
                                      {line.count2
                                        ? resolveCountQuantityDisplay(line.count2)
                                        : '—'}
                                    </td>
                                    <td className="fir-num">
                                      {line.count2 ? fmtNum(line.count2.lineValue) : '—'}
                                    </td>
                                    <td className="fir-num fir-qty-pair">
                                      {line.difference?.quantityDisplay ||
                                        formatQuantityDisplay(
                                          line.difference?.quantity,
                                          line.difference?.quantity2
                                        )}
                                    </td>
                                    <td className="fir-num">{fmtNum(line.difference?.value)}</td>
                                    <td className="fir-num">{fmtPct(line.difference?.variancePct)}</td>
                                    <td>
                                      <div className="fir-movement-cell">
                                        <span className={`fir-dir fir-dir-${dir}`}>
                                          <DirectionIcon direction={dir} />
                                          {titleCaseMovement(dir)}
                                        </span>
                                        {isBrandReport && line.siteMovementSummary ? (
                                          <div className="fir-site-movement-flags">
                                            <span className="fir-site-movement-prefix">Site:</span>
                                            {MOVEMENT_HELP_ITEMS.filter((item) => {
                                              if (
                                                Number(line.siteMovementSummary[item.key]) <= 0
                                              ) {
                                                return false;
                                              }
                                              if (item.key !== 'unchanged') return true;
                                              return !MOVEMENT_HELP_ITEMS.some(
                                                (other) =>
                                                  other.key !== 'unchanged' &&
                                                  Number(
                                                    line.siteMovementSummary[other.key]
                                                  ) > 0
                                              );
                                            }).map((item) => (
                                              <span
                                                key={item.key}
                                                className={`fir-site-movement-badge fir-dir-${item.key}`}
                                              >
                                                {item.label} ×
                                                {Number(line.siteMovementSummary[item.key])}
                                              </span>
                                            ))}
                                          </div>
                                        ) : null}
                                      </div>
                                    </td>
                                  </>
                                )}
                              </tr>
                              {expanded && (
                                <tr className="fir-detail-row">
                                  <td colSpan={detailColSpan}>
                                    <div className="fir-detail-grid">
                                      <div>
                                        <span className="fir-meta-label">Description</span>
                                        <p>{line.itemDescription || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="fir-meta-label">Vendor</span>
                                        <p>{line.vendor || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="fir-meta-label">Location</span>
                                        <p>{line.location || '—'}</p>
                                      </div>
                                      <div>
                                        <span className="fir-meta-label">Units</span>
                                        <p>
                                          {line.unit1 || '—'}
                                          {line.unit2 ? ` / ${line.unit2}` : ''}
                                        </p>
                                      </div>
                                      {!isCompare && (
                                        <>
                                          <div>
                                            <span className="fir-meta-label">Notes</span>
                                            <p>{line.notes || '—'}</p>
                                          </div>
                                          <div>
                                            <span className="fir-meta-label">Manager acknowledgement</span>
                                            <p>{line.managerAckComment || '—'}</p>
                                          </div>
                                          <div>
                                            <span className="fir-meta-label">Source</span>
                                            <p>
                                              {line.fromArchiveSnapshot
                                                ? 'Archived snapshot'
                                                : report.count?.source === 'archive'
                                                  ? 'Archived'
                                                  : 'Live'}
                                            </p>
                                          </div>
                                        </>
                                      )}
                                      {isCompare && (
                                        <>
                                          <div>
                                            <span className="fir-meta-label">Count 1 notes</span>
                                            <p>{line.notesCount1 || '—'}</p>
                                          </div>
                                          <div>
                                            <span className="fir-meta-label">Count 2 notes</span>
                                            <p>{line.notesCount2 || '—'}</p>
                                          </div>
                                          <div>
                                            <span className="fir-meta-label">Ack (Count 1)</span>
                                            <p>{line.managerAckComment1 || '—'}</p>
                                          </div>
                                          <div>
                                            <span className="fir-meta-label">Ack (Count 2)</span>
                                            <p>{line.managerAckComment2 || '—'}</p>
                                          </div>
                                          <div>
                                            <span className="fir-meta-label">Flags</span>
                                            <p>
                                              {[
                                                line.priority ? 'Priority' : null,
                                                line.isPriorityZero ? 'Priority zero-count' : null,
                                                line.isHighValue ? 'High value' : null
                                              ]
                                                .filter(Boolean)
                                                .join(', ') || '—'}
                                            </p>
                                          </div>
                                          <div>
                                            <span className="fir-meta-label">Snapshot</span>
                                            <p>
                                              {line.fromArchiveSnapshot
                                                ? 'Uses archived snapshot data where available'
                                                : 'Live / non-snapshot fields'}
                                            </p>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                    {isBrandReport &&
                                    Array.isArray(line.siteBreakdown) &&
                                    line.siteBreakdown.length > 0 ? (
                                      <div className="fir-site-breakdown">
                                        <div className="fir-site-breakdown-header">
                                          Site contributions
                                        </div>
                                        <div className="fir-site-breakdown-scroll">
                                          <table className="fir-site-breakdown-table">
                                            <thead>
                                              <tr>
                                                <th>Site</th>
                                                {isCompare ? (
                                                  <>
                                                    <th className="fir-num">Count 1 Qty</th>
                                                    <th className="fir-num">Count 1 Value</th>
                                                    <th className="fir-num">Count 2 Qty</th>
                                                    <th className="fir-num">Count 2 Value</th>
                                                    <th>Movement</th>
                                                  </>
                                                ) : (
                                                  <>
                                                    <th className="fir-num">Qty</th>
                                                    <th className="fir-num">Value</th>
                                                  </>
                                                )}
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {line.siteBreakdown.map((site) => {
                                                const siteDir = site.movement || '';
                                                return (
                                                  <tr key={site.siteId || site.siteName}>
                                                    <td>{site.siteName || '—'}</td>
                                                    {isCompare ? (
                                                      <>
                                                        <td className="fir-num fir-qty-pair">
                                                          {site.count1QuantityDisplay ?? '—'}
                                                        </td>
                                                        <td className="fir-num">
                                                          {site.count1Value == null
                                                            ? '—'
                                                            : fmtNum(site.count1Value)}
                                                        </td>
                                                        <td className="fir-num fir-qty-pair">
                                                          {site.count2QuantityDisplay ?? '—'}
                                                        </td>
                                                        <td className="fir-num">
                                                          {site.count2Value == null
                                                            ? '—'
                                                            : fmtNum(site.count2Value)}
                                                        </td>
                                                        <td>
                                                          <span
                                                            className={`fir-dir fir-dir-${siteDir || 'unchanged'}`}
                                                          >
                                                            <DirectionIcon direction={siteDir} />
                                                            {titleCaseMovement(siteDir)}
                                                          </span>
                                                        </td>
                                                      </>
                                                    ) : (
                                                      <>
                                                        <td className="fir-num fir-qty-pair">
                                                          {site.quantityDisplay ||
                                                            formatQuantityDisplay(
                                                              site.countedQuantity,
                                                              site.countedQuantity2
                                                            )}
                                                        </td>
                                                        <td className="fir-num">
                                                          {fmtNum(site.lineValue)}
                                                        </td>
                                                      </>
                                                    )}
                                                  </tr>
                                                );
                                              })}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    ) : null}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {isAdmin && <BulkExportFullCounts />}
    </div>
  );
};

export default FullInventoryReport;
